/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, Save, Sparkles, RefreshCw, ClipboardCheck, BarChart3, Pencil, X } from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Textarea } from "./components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const adminHeaders = () => ({ "X-Admin-Password": sessionStorage.getItem("adminPassword") || "" });
const LETTERS = ["A", "B", "C", "D"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

const emptyQuestion = () => ({ question: "", choices: ["", "", "", ""], answer: 0, explication: "" });

const QuestionEditor = ({ q, index, onChange, onRemove }) => (
  <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2" data-testid={`quiz-question-editor-${index}`}>
    <div className="flex items-start gap-2">
      <span className="font-bold text-[#003087] text-sm mt-2">{index + 1}.</span>
      <Textarea value={q.question} onChange={(e) => onChange({ ...q, question: e.target.value })} placeholder="Énoncé de la question" rows={2} className="text-sm bg-white" data-testid={`quiz-question-text-${index}`} />
      <Button size="sm" variant="ghost" onClick={onRemove} className="text-red-600" aria-label="Supprimer"><Trash2 className="h-4 w-4" /></Button>
    </div>
    <div className="grid md:grid-cols-2 gap-2 pl-5">
      {q.choices.map((c, ci) => (
        <label key={ci} className={`flex items-center gap-2 p-2 rounded-md border bg-white ${q.answer === ci ? "border-green-500" : "border-gray-200"}`}>
          <input type="radio" name={`ans-${index}`} checked={q.answer === ci} onChange={() => onChange({ ...q, answer: ci })} className="accent-green-600" title="Bonne réponse" />
          <span className="font-mono text-xs text-gray-500">{LETTERS[ci]}</span>
          <Input value={c} onChange={(e) => onChange({ ...q, choices: q.choices.map((x, i) => (i === ci ? e.target.value : x)) })} className="h-8 text-sm" placeholder={`Choix ${LETTERS[ci]}`} />
        </label>
      ))}
    </div>
    <Input value={q.explication} onChange={(e) => onChange({ ...q, explication: e.target.value })} placeholder="Explication de la bonne réponse (facultatif)" className="ml-5 w-[calc(100%-1.25rem)] h-8 text-sm bg-white" />
  </div>
);

const QuizEditor = ({ quiz, onSaved, onCancel }) => {
  const [title, setTitle] = useState(quiz.title);
  const [questions, setQuestions] = useState(quiz.questions);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (questions.some((q) => !q.question.trim() || q.choices.some((c) => !c.trim()))) {
      toast.error("Chaque question doit avoir un énoncé et 4 choix remplis");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/quiz/${quiz.id}`, { title, questions }, { headers: adminHeaders() });
      toast.success("Quiz enregistré");
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-2 border-[#F07B1D]" data-testid="quiz-editor">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="max-w-xl font-bold" data-testid="quiz-editor-title" />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onCancel}><X className="h-4 w-4 mr-1" /> Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold" data-testid="quiz-editor-save">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </div>
        <CardDescription>Cochez le bouton radio de la bonne réponse. {questions.length} question(s).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {questions.map((q, i) => (
          <QuestionEditor key={q.id || i} q={q} index={i} onChange={(nq) => setQuestions(questions.map((x, j) => (j === i ? nq : x)))} onRemove={() => setQuestions(questions.filter((_, j) => j !== i))} />
        ))}
        <Button variant="outline" onClick={() => setQuestions([...questions, emptyQuestion()])} data-testid="quiz-editor-add-question">
          <Plus className="h-4 w-4 mr-1" /> Ajouter une question
        </Button>
      </CardContent>
    </Card>
  );
};

const ProgressionTable = ({ rows }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-[#003087] flex items-center gap-2"><BarChart3 className="h-5 w-5" /> Progression des apprenants</CardTitle>
      <CardDescription>{rows.length} parcours démarré(s) — score le plus élevé par quiz, quiz final et certificat.</CardDescription>
    </CardHeader>
    <CardContent>
      {rows.length === 0 ? <p className="text-sm text-gray-500 italic">Aucune progression enregistrée.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="progression-table">
            <thead><tr className="text-left text-xs uppercase text-gray-500 border-b">
              <th className="py-2 pr-3">Apprenant</th><th className="py-2 pr-3">Formation</th><th className="py-2 pr-3">Modules</th><th className="py-2 pr-3">Quiz</th><th className="py-2 pr-3">Progression</th><th className="py-2 pr-3">Dernière activité</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.apprenant_id}-${r.formation_code}`} className="border-b border-gray-100" data-testid={`progression-row-${r.formation_code}`}>
                  <td className="py-2 pr-3"><p className="font-semibold text-[#003087]">{r.prenom} {r.nom}</p><p className="text-xs text-gray-500">{r.email}</p></td>
                  <td className="py-2 pr-3"><Badge className="bg-[#003087] text-white mr-1">{r.formation_code}</Badge><span className="text-xs">{r.formation_title}</span></td>
                  <td className="py-2 pr-3">{r.nb_modules_done}/{r.nb_modules}</td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1">
                      {r.quiz_results.map((q) => (
                        <Badge key={q.quiz_id} variant="outline" className={`text-[10px] ${q.passed ? "border-green-500 text-green-700" : "border-red-300 text-red-600"}`} title={q.title}>
                          {q.module_id ? q.module_id.match(/M\d+$/)?.[0] : "Final"} · {q.best_pct} % ({q.attempts} ess.)
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <span className="font-bold text-[#003087]">{r.pct} %</span>
                    {r.completed_at && <Badge className="ml-2 bg-green-600 text-white text-[10px]">Certifié</Badge>}
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-500">{fmt(r.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

export const QuizSection = () => {
  const [data, setData] = useState({ formations: [], quizzes: [] });
  const [progression, setProgression] = useState([]);
  const [code, setCode] = useState("");
  const [editing, setEditing] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [q, p] = await Promise.all([
        axios.get(`${API}/admin/quiz`, { headers: adminHeaders() }),
        axios.get(`${API}/admin/progression`, { headers: adminHeaders() }),
      ]);
      setData(q.data);
      setProgression(p.data);
      if (!code && q.data.formations[0]) setCode(q.data.formations[0].code);
    } catch { toast.error("Chargement impossible"); }
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const formation = data.formations.find((f) => f.code === code);
  const quizzes = data.quizzes.filter((q) => q.formation_code === code);
  const quizFor = (moduleId) => quizzes.find((q) => (q.module_id || null) === moduleId);

  const generate = async () => {
    if (quizzes.length && !window.confirm("Régénérer remplacera tous les quiz de cette formation (les résultats des apprenants sont conservés). Continuer ?")) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/quiz/generate/${code}`, {}, { headers: adminHeaders(), timeout: 180000 });
      toast.success(`${res.data.quiz_created} quiz générés par IA`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Génération impossible");
    } finally { setGenerating(false); }
  };

  const createEmpty = async (moduleId, title) => {
    try {
      const res = await axios.post(`${API}/admin/quiz`, { formation_code: code, module_id: moduleId, title, questions: [{ ...emptyQuestion(), question: "Nouvelle question", choices: ["Choix A", "Choix B", "Choix C", "Choix D"] }] }, { headers: adminHeaders() });
      await load();
      setEditing(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Création impossible"); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Supprimer « ${q.title} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/quiz/${q.id}`, { headers: adminHeaders() });
      toast.success("Quiz supprimé");
      load();
    } catch { toast.error("Suppression impossible"); }
  };

  const QuizRow = ({ label, moduleId }) => {
    const q = quizFor(moduleId);
    return (
      <div className="flex flex-wrap items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white" data-testid={`quiz-row-${moduleId || "final"}`}>
        <ClipboardCheck className={`h-5 w-5 shrink-0 ${moduleId ? "text-[#2196F3]" : "text-[#F07B1D]"}`} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#003087] text-sm">{label}</p>
          <p className="text-xs text-gray-500">{q ? `${q.questions.length} question(s) · ${q.source === "ia" ? "généré par IA" : "édité"} · maj ${fmt(q.updated_at)}` : "Aucun quiz"}</p>
        </div>
        {q ? (
          <>
            <Button size="sm" variant="outline" onClick={() => setEditing(q)} data-testid={`quiz-edit-${moduleId || "final"}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Modifier</Button>
            <Button size="sm" variant="outline" onClick={() => remove(q)} className="text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => createEmpty(moduleId, moduleId ? `Quiz — ${label}` : `Quiz final de validation — ${formation?.title}`)}><Plus className="h-3.5 w-3.5 mr-1" /> Créer</Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="quiz-section">
      {editing ? (
        <QuizEditor quiz={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-[#003087] flex items-center gap-2"><ClipboardCheck className="h-5 w-5" /> Quiz QCM par formation</CardTitle>
                <CardDescription>Un quiz par module (3-4 questions) + un quiz final de validation (seuil {data.seuil ?? 80} %). Générés par IA puis modifiables.</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[260px]">
                <Label>Formation</Label>
                <select value={code} onChange={(e) => setCode(e.target.value)} className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm" data-testid="quiz-formation-select">
                  {data.formations.map((f) => {
                    const n = data.quizzes.filter((q) => q.formation_code === f.code).length;
                    return <option key={f.code} value={f.code}>{f.code} — {f.title} ({n} quiz)</option>;
                  })}
                </select>
              </div>
              <Button onClick={generate} disabled={generating || !code} className="bg-[#003087] hover:bg-[#002366] text-white" data-testid="quiz-generate-btn">
                <Sparkles className="h-4 w-4 mr-1" /> {generating ? "Génération IA en cours…" : quizzes.length ? "Régénérer par IA" : "Générer par IA"}
              </Button>
            </div>
            {formation && (
              <div className="space-y-2">
                {formation.modules.map((m) => <QuizRow key={m.id} label={m.title} moduleId={m.id} />)}
                <QuizRow label="Quiz final de validation (certificat)" moduleId={null} />
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <ProgressionTable rows={progression} />
    </div>
  );
};
